import React, { useState } from 'react';
import { calendarAPI, CalendarItem, CreateEventPayload } from '@/services/calendarApi';
import { EVENT_TYPE_LABELS } from '@/components/calendar/eventTypes';
import { toISO } from '@/components/calendar/EventModal/types';
import { addLocalMinutes, initialEventStart } from '@/components/calendar/EventModal/dateTime';
import DisclosureSection from '@/components/calendar/EventModal/DisclosureSection';
import EventDateTimeFields from '@/components/calendar/EventModal/EventDateTimeFields';

interface PlayerAddEventModalProps {
  day?: Date;
  onSaved: () => void;
  onClose: () => void;
}

const PlayerAddEventModal: React.FC<PlayerAddEventModalProps> = ({ day, onSaved, onClose }) => {
  const initialStart = initialEventStart(day);
  const [title, setTitle] = useState('');
  const [type, setType] = useState<CalendarItem['type']>('other');
  const [start, setStart] = useState(initialStart);
  const [end, setEnd] = useState(() => addLocalMinutes(initialStart, 60));
  const [endIsAutomatic, setEndIsAutomatic] = useState(true);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const changeStart = (value: string) => {
    setStart(value);
    if (endIsAutomatic) setEnd(addLocalMinutes(value, 60));
  };

  const changeEnd = (value: string) => {
    setEnd(value);
    setEndIsAutomatic(false);
  };

  const optionalDetailsSummary = location && description
    ? 'Location and description'
    : location
      ? 'Location'
      : description
        ? 'Description'
        : 'No optional details';

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!start) {
      setError('Start time is required');
      return;
    }
    setSaving(true);
    setError('');

    const payload: CreateEventPayload = {
      title: title.trim(),
      type,
      startsAt: toISO(start),
      endsAt: end ? toISO(end) : undefined,
      location: location || undefined,
      description: description || undefined,
    };

    try {
      await calendarAPI.createPersonal(payload);
      onSaved();
    } catch (err: any) {
      setError(err?.message || 'Failed to save event');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white p-5 shadow-xl dark:bg-gray-900 dark:text-gray-100 sm:max-w-lg sm:rounded-lg sm:p-6">
        <div className="sm:hidden mx-auto mb-4 h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />

        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Add Personal Event</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</div>
        )}

        <form onSubmit={handleSave} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            <div>
              <label htmlFor="personal-event-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title
              </label>
              <input
                id="personal-event-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              />
            </div>

            <div>
              <label htmlFor="personal-event-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type
              </label>
              <select
                id="personal-event-type"
                value={type}
                onChange={(e) => setType(e.target.value as CalendarItem['type'])}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              >
                {(Object.keys(EVENT_TYPE_LABELS) as CalendarItem['type'][]).map((key) => (
                  <option key={key} value={key}>
                    {EVENT_TYPE_LABELS[key]}
                  </option>
                ))}
              </select>
            </div>

            <EventDateTimeFields
              start={start}
              end={end}
              allDay={false}
              onStartChange={changeStart}
              onEndChange={changeEnd}
            />

            <DisclosureSection
              id="personal-event-optional-details"
              title="Optional details"
              summary={optionalDetailsSummary}
              open={detailsOpen}
              onOpenChange={setDetailsOpen}
            >
              <div className="space-y-4">
                <div>
                  <label htmlFor="personal-event-location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Location
                  </label>
                  <input
                    id="personal-event-location"
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label htmlFor="personal-event-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    id="personal-event-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>
            </DisclosureSection>
          </div>

          <div className="mt-4 flex shrink-0 flex-col-reverse gap-2 border-t border-gray-200 pt-4 dark:border-gray-700 sm:flex-row sm:justify-end sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="w-full sm:w-auto px-4 py-2 rounded-md bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlayerAddEventModal;
